'use client'

import { useState, useEffect, useRef, type RefObject } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useRouter, usePathname } from 'next/navigation'
import Link from '@/components/ui/Link'
import { Icon } from '@/icons/Icon'
import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, cn, isPopulatedObject } from '@/lib/utils'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { easeMedium } from '@/lib/transitions'
import { useScrollGradients } from '@/hooks/useScrollGradients'
import { queryPayload } from '@/lib/payloadClient'
import { createLogger } from '@/lib/logger'
import type { App, Category, Deal, Stack } from '@/payload-types'

export type CollectionTypes = 'category' | 'app' | 'deal' | 'stack'

const logger = createLogger('PageSelect')

type CategoryOption = Pick<Category, 'id' | 'name' | 'slug' | 'icon'>
export type PageSelectOption = App | Category | CategoryOption | Stack

type PageSelectProps = {
  type: CollectionTypes
  selectedLabel?: string
  center?: boolean
}

type CollectionProps = {
  apps: CollectionItem
  best: CollectionItem
  alternatives: CollectionItem
  deals: CollectionItem
  stacks: CollectionItem
}

type CollectionItem = {
  label: string
  id: CollectionTypes
  url: string
}

type PageSelectListProps = {
  top?: number
  options: PageSelectOption[]
  selected: number
  collection: CollectionItem
  sidebar?: boolean
  meta?: boolean
  center?: boolean
}

type PageSelectItemProps = {
  option: PageSelectOption
  selected: number
  focus?: boolean
  collection: CollectionItem
}

type PageSelectSearchProps = {
  setSearchAction: (search: string) => void
  autoFocus?: boolean
  collection: CollectionItem
}

export const collections: CollectionProps = {
  apps: {
    label: 'apps',
    id: 'app',
    url: 'apps',
  },
  best: {
    label: 'categories',
    id: 'category',
    url: 'best',
  },
  alternatives: {
    label: 'apps',
    id: 'app',
    url: 'alternatives',
  },
  deals: {
    label: 'deals',
    id: 'deal',
    url: 'deals',
  },
  stacks: {
    label: 'stacks',
    id: 'stack',
    url: 'stacks',
  },
} as const

const APP_FIELDS = {
  name: true,
  slug: true,
  icon: true,
  glyph: true,
  featuredOrder: true,
  brandBackgroundColor: true,
  brandPrimaryColor: true,
  brandSecondaryColor: true,
  brandTertiaryColor: true,
} as const

const DEAL_FIELDS = {
  app: true,
  name: true,
  order: true,
  createdAt: true,
  updatedAt: true,
  link: true,
  unitType: true,
  isReward: true,
  eligibility: true,
  quantity: true,
  claimed: true,
  terms: true,
  durationDays: true,
  startDate: true,
  endDate: true,
  isFeatured: true,
  featuredOrder: true,
} as const

const MEDIA_FIELDS = {
  alt: true,
  filename: true,
  url: true,
  coveragePercent: true,
  xOffsetPercent: true,
  yOffsetPercent: true,
} as const

function sortFeaturedThenName<T extends { name?: string | null }>(docs: T[]): T[] {
  return [...docs].sort((a, b) => {
    const aOrder = 'featuredOrder' in a ? (a.featuredOrder as number | null) : null
    const bOrder = 'featuredOrder' in b ? (b.featuredOrder as number | null) : null
    if (aOrder !== null && bOrder === null) return -1
    if (aOrder === null && bOrder !== null) return 1
    if (aOrder !== null && bOrder !== null && aOrder !== bOrder) return aOrder - bOrder
    return (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
  })
}

async function fetchAppOptions(): Promise<App[]> {
  const { docs: reviewDocs } = await queryPayload({
    collection: 'review',
    select: { app: true },
    populate: {
      app: APP_FIELDS,
      media: MEDIA_FIELDS,
    },
    depth: 2,
    pagination: false,
    where: { _status: { equals: 'published' } },
    sort: ['featuredOrder', 'name'],
    cacheKey: 'nav:select:apps',
  })
  const apps = reviewDocs.flatMap((doc) => (isPopulatedObject<App>(doc.app) ? [doc.app] : []))
  const unique = apps.filter((app, i, self) => i === self.findIndex((a) => a.id === app.id))
  return sortFeaturedThenName(unique)
}

async function fetchBestOptions(): Promise<Category[]> {
  const { docs: categoryDocs } = await queryPayload({
    collection: 'category',
    select: { name: true, slug: true, icon: true, featuredOrder: true },
    depth: 1,
    pagination: false,
    sort: ['featuredOrder', 'name'],
    cacheKey: 'nav:select:best',
  })
  return sortFeaturedThenName(categoryDocs as Category[])
}

async function fetchAlternativeOptions(): Promise<App[]> {
  const { docs: alternativeDocs } = await queryPayload({
    collection: 'alternative',
    select: { app: true },
    populate: {
      app: APP_FIELDS,
      media: MEDIA_FIELDS,
    },
    depth: 2,
    pagination: false,
    where: { app: { exists: true } },
    sort: ['featuredOrder', '-createdAt'],
    cacheKey: 'nav:select:alternatives',
  })
  const apps = alternativeDocs
    .map((doc) => doc.app)
    .filter((app): app is App => isPopulatedObject<App>(app))
  return sortFeaturedThenName(apps)
}

async function fetchDealOptions(): Promise<App[]> {
  const { docs: dealDocs } = await queryPayload({
    collection: 'deal',
    select: DEAL_FIELDS,
    populate: {
      app: APP_FIELDS,
      media: MEDIA_FIELDS,
    },
    depth: 3,
    pagination: false,
    where: {
      _status: { equals: 'published' },
      'app.review._status': { equals: 'published' },
    },
    sort: ['featuredOrder', 'name'],
    cacheKey: 'nav:select:deals',
  })
  const visibleDeals = determineDealsToDisplay(dealDocs as Deal[])

  const appDealsMap = new Map<number, Deal[]>()
  visibleDeals.forEach((deal) => {
    if (isPopulatedObject<App>(deal.app)) {
      const appId = deal.app.id
      if (!appDealsMap.has(appId)) {
        appDealsMap.set(appId, [])
      }
      appDealsMap.get(appId)!.push(deal)
    }
  })

  const apps = Array.from(appDealsMap.entries()).map(([_appId, deals]) => {
    const app = deals[0].app as App
    return {
      ...app,
      deals: {
        docs: deals,
      },
    } as App
  })

  return sortFeaturedThenName(apps)
}

async function fetchStackOptions(): Promise<Stack[]> {
  const { docs: stackDocs } = await queryPayload({
    collection: 'stack',
    select: {
      name: true,
      companyStackApp: true,
      slug: true,
      icon: true,
      featuredOrder: true,
      stackApps: true,
    },
    populate: {
      stack_apps_app: { app: true },
      app: APP_FIELDS,
      media: MEDIA_FIELDS,
    },
    depth: 4,
    pagination: false,
    where: {
      _status: { equals: 'published' },
      type: { in: ['verified', 'expert'] },
    },
    sort: ['featuredOrder', 'name'],
    cacheKey: 'nav:select:stacks',
  })
  return sortFeaturedThenName(
    (stackDocs as Stack[]).map((stack) => ({
      ...stack,
      stackApps: {
        ...stack.stackApps,
        docs: stack.stackApps?.docs?.slice(0, 1) ?? [],
      },
    })),
  )
}

function convertToDealLabel(app: string, totalDeals: number) {
  return `${app} Deal${totalDeals !== 1 ? 's' : ''}`
}

export default function PageSelect({ type, selectedLabel, center }: PageSelectProps) {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<Array<App | Category | Stack>>([])
  const [selected, setSelected] = useState<App | Category | Stack | null>(null)
  const [loading, setLoading] = useState(true)
  const path = usePathname()
  const collection = collections[type as keyof CollectionProps]

  const isApp = collection?.id === 'app'
  const isDeal = collection?.id === 'deal'
  const isCategory = collection?.id === 'category'
  const isStack = collection?.id === 'stack'

  useEffect(() => {
    let isMounted = true

    async function fetchAndSetOptions() {
      if (!collection?.url) return

      try {
        if (isMounted) setLoading(true)

        const fetchers: Record<string, () => Promise<(App | Category | Stack)[]>> = {
          apps: fetchAppOptions,
          best: fetchBestOptions,
          alternatives: fetchAlternativeOptions,
          deals: fetchDealOptions,
          stacks: fetchStackOptions,
        }

        const fetcher = fetchers[collection.url]
        if (!fetcher) return

        const docs = await fetcher()
        if (isMounted) setOptions(docs)
      } catch (error) {
        if (isMounted) {
          logger.error('Error fetching options:', error)
          setOptions([])
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchAndSetOptions()

    return () => {
      isMounted = false
    }
  }, [collection])

  useEffect(() => {
    const pathSlug = path.split('/').pop()

    const option = options.find((c) => {
      const item = c as Category | App
      return item.slug === pathSlug
    })

    if (!option) return
    setSelected(option)
  }, [path, options, isDeal])

  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = open ? 'hidden' : prevOverflow

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }

      // Check if any input is currently focused
      const activeElement = document.activeElement
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable)

      if (isInputFocused) return

      if (e.key === 'f' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && e.isTrusted) {
        e.preventDefault()
        setOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => {
      window.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  if (!collection) {
    logger.error(`Invalid collection type: ${type}`)
    return null
  }

  const selectedApp = isApp || isDeal ? (selected as App) : null
  const selectedCategory = isCategory ? (selected as Category) : null
  const selectedStack = isStack ? (selected as Stack) : null
  const companyApp =
    selectedStack && isPopulatedObject<App>(selectedStack.companyStackApp)
      ? selectedStack.companyStackApp
      : null
  const firstSelectedStackApp =
    selectedStack?.stackApps?.docs?.[0] &&
    typeof selectedStack.stackApps.docs[0] !== 'number' &&
    isPopulatedObject<App>(selectedStack.stackApps.docs[0].app)
      ? selectedStack.stackApps.docs[0].app
      : null

  const selectedStackDisplayApp = companyApp ?? firstSelectedStackApp

  const { backgroundColor } =
    isApp || isDeal
      ? getBrandColors(selectedApp)
      : isStack
        ? getBrandColors(selectedStackDisplayApp)
        : { backgroundColor: '' }

  return (
    <>
      <div className="relative z-50 flex min-w-0 flex-1">
        <div
          onClick={() => setOpen(!open)}
          className={cn(
            'flex h-8 cursor-pointer items-center gap-2.5 rounded-lg border border-white/15 bg-white/5 px-3 transition-all hover:border-primary-500/80 hover:shadow-[0_0_25px_5px_rgba(255,52,101,0.25)]',
            open && 'border-primary-500/80 shadow-[0_0_25px_5px_rgba(255,52,101,0.25)]',
          )}
        >
          {selected ? (
            selectedApp ? (
              <AppIcon
                glyph={selectedApp.glyph}
                icon={selectedApp.icon}
                name={selectedApp.name}
                background={backgroundColor}
                size={16}
                invert={true}
              />
            ) : selectedStack ? (
              selectedStack.icon ? (
                <Icon name={selectedStack.icon} size={12} strokeWidth={1} />
              ) : selectedStackDisplayApp ? (
                <AppIcon
                  glyph={selectedStackDisplayApp.glyph}
                  icon={selectedStackDisplayApp.icon}
                  name={selectedStackDisplayApp.name}
                  background={backgroundColor}
                  size={16}
                  invert={true}
                />
              ) : null
            ) : (
              selectedCategory && (
                <Icon name={selectedCategory.icon as string} size={12} strokeWidth={1} />
              )
            )
          ) : null}
          <span className="line-clamp-1 text-14 text-white">
            {isDeal && selected
              ? convertToDealLabel(selected.name, (selected as App).deals?.docs?.length ?? 0)
              : selectedLabel || selected?.name || 'Select'}
          </span>
          <div
            className={cn(
              '-rotate-90 text-white/40 transition-transform ease-in-out',
              open && 'rotate-90',
            )}
          >
            <Icon name="chevron-left" size={12} />
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <PageSelectList
              options={options}
              selected={selected?.id ?? 0}
              collection={collection}
              meta={true}
              center={center}
            />
          )}
        </AnimatePresence>
      </div>

      {open && (
        <div onClick={() => setOpen(false)} className="fixed left-0 top-0 z-40 h-full w-full"></div>
      )}
    </>
  )
}

export function PageSelectList({
  top,
  options,
  selected,
  collection,
  sidebar = false,
  meta = false,
  center = false,
}: PageSelectListProps) {
  const router = useRouter()
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const categoryRef = useRef<HTMLDivElement[]>([])

  const { showTopGradient, showBottomGradient } = useScrollGradients(
    containerRef as RefObject<HTMLElement>,
  )
  const ignoreHoverRef = useRef(false)

  const filteredOptions = options.filter((option) =>
    (collection.id === 'deal'
      ? convertToDealLabel(option.name, (option as App).deals?.docs?.length ?? 0)
      : option.name
    )
      ?.toLowerCase()
      .includes(search.toLowerCase()),
  )

  const filteredCount = filteredOptions.length

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      if (
        activeElement &&
        (activeElement.getAttribute('data-chat-input') === 'true' ||
          activeElement.closest('[data-chat-search]'))
      ) {
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCount - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        if (selectedIndex < 0 || selectedIndex >= filteredCount) return
        e.preventDefault()
        e.stopPropagation()
        const selectedItem = filteredOptions[selectedIndex]
        router.push(`/${collection.url}/${selectedItem.slug}`)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex, filteredCount, filteredOptions, router, collection?.url])

  useEffect(() => {
    if (selectedIndex > 0) {
      categoryRef.current[selectedIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedIndex])

  useEffect(() => {
    setSelectedIndex(0)
    ignoreHoverRef.current = true
  }, [search])

  useEffect(() => {
    const handleMouseMove = () => {
      if (ignoreHoverRef.current) {
        ignoreHoverRef.current = false
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, marginTop: 10 }}
      animate={{ opacity: 1, marginTop: 0 }}
      exit={{ opacity: 0, marginTop: 10 }}
      transition={easeMedium}
      style={
        sidebar && top ? { top: `${top}px`, maxHeight: `calc(100dvh - ${top + 8}px)` } : undefined
      }
      className={cn(
        // Base styles
        'hide-scrollbar fixed z-45 h-[calc(100dvh-88px)] md:h-fit',

        'flex w-[calc(100vw-24px)] min-w-[280px] cursor-pointer flex-col items-center overflow-hidden',
        'border border-white/15 bg-black/90 sm:bg-black/70',
        'before:absolute before:inset-0 before:-z-10 before:backdrop-blur',
        'shadow-[0px_32px_72px_-20px_rgba(0,0,0,1)]',
        center ? 'left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0' : 'left-auto',

        // Position variants
        sidebar
          ? [
              'top-[129px] max-h-[calc(100dvh-123px)] w-full',
              'md:right-[74px] md:max-w-[320px] md:rounded-2xl',
              'xl:right-[82px]',
              '2xl:right-[102px]',
            ]
          : [
              'top-10 rounded-2xl sm:max-w-[320px]',
              'md:absolute md:left-0 md:translate-x-0',
              meta
                ? 'absolute max-h-[535px] tall:max-h-[635px]'
                : 'left-1/2 max-h-[calc(100dvh-280px)] -translate-x-1/2',
            ],
      )}
    >
      <PageSelectSearch setSearchAction={setSearch} autoFocus={true} collection={collection} />
      <div
        className={cn(
          'relative flex h-full w-full flex-col overflow-hidden',
          'before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-10 before:h-8 before:w-full before:bg-gradient-to-b before:from-black before:to-transparent before:opacity-0 before:transition-opacity before:content-[""]',
          'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:z-10 after:h-8 after:w-full after:bg-gradient-to-t after:from-black after:to-transparent after:opacity-0 after:transition-opacity after:content-[""]',
          showTopGradient && 'before:opacity-100',
          showBottomGradient && 'after:opacity-100',
        )}
      >
        <div
          ref={containerRef}
          className="flex min-h-0 w-full flex-1 flex-col gap-2 overflow-y-auto p-3 md:gap-0.5"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const focus = selectedIndex === index

              return (
                <div
                  key={option.id}
                  onMouseEnter={() => {
                    if (ignoreHoverRef.current) return
                    setSelectedIndex(index)
                  }}
                  onMouseLeave={() => {
                    if (ignoreHoverRef.current) return
                    setSelectedIndex(-1)
                  }}
                  ref={(el) => {
                    categoryRef.current[index] = el as HTMLDivElement
                  }}
                >
                  <PageSelectItem
                    option={option}
                    selected={selected}
                    focus={focus}
                    collection={collection}
                  />
                </div>
              )
            })
          ) : (
            <div className="flex h-7 w-full shrink-0 items-center px-1.5 text-13 font-medium text-white/40">
              No results
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export function PageSelectItem({ option, selected, focus, collection }: PageSelectItemProps) {
  const label =
    collection.id === 'deal'
      ? convertToDealLabel(option.name, (option as App).deals?.docs?.length ?? 0)
      : option.name

  return (
    <Link
      href={`/${collection.url}/${option.slug}`}
      aria-current={option.id === selected ? 'page' : undefined}
      className={cn(
        'group flex h-11 w-full shrink-0 items-center gap-4 rounded-lg border border-white/0 pl-1.5 pr-1 text-13 font-medium text-white transition-colors duration-300 md:h-10',
        (option.id === selected || focus) && 'bg-white/10',
      )}
    >
      {collection.id === 'category' ? (
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 text-zinc-500 transition-colors duration-300',
            (option.id === selected || focus) && 'bg-white/10',
          )}
        >
          <Icon name={(option as Category).icon as string} size={14} strokeWidth={1} />
        </div>
      ) : collection.id === 'stack' ? (
        (() => {
          const stack = option as Stack
          const companyApp = stack.companyStackApp as App
          const firstApp =
            stack.stackApps?.docs?.[0] !== null &&
            stack.stackApps?.docs?.[0] !== undefined &&
            typeof stack.stackApps.docs[0] !== 'number'
              ? (stack.stackApps.docs[0].app as App)
              : null
          const appObject = companyApp ?? firstApp

          return stack.icon ? (
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 text-zinc-500 transition-colors duration-300',
                (option.id === selected || focus) && 'bg-white/10',
              )}
            >
              <Icon name={stack.icon} size={14} strokeWidth={1} />
            </div>
          ) : appObject ? (
            <AppIcon
              glyph={appObject.glyph}
              icon={appObject.icon}
              name={appObject.name}
              background={appObject.brandBackgroundColor}
              size={28}
              invert={true}
            />
          ) : null
        })()
      ) : (
        <AppIcon
          glyph={(option as App).glyph}
          icon={(option as App).icon}
          name={option.name}
          background={(option as App).brandBackgroundColor}
          size={28}
          invert={true}
        />
      )}
      <span className="w-full whitespace-nowrap text-15 transition-colors duration-300 md:text-13">
        {label}
      </span>

      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center opacity-0 transition-opacity',
          (focus || option.id === selected) && 'opacity-100',
          option.id === selected ? 'text-primary-500' : 'text-gray-500',
        )}
      >
        <Icon name={option.id === selected ? 'check' : 'return-key'} size={12} />
      </div>
    </Link>
  )
}

export function PageSelectSearch({
  setSearchAction,
  autoFocus,
  collection,
}: PageSelectSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus && window.innerWidth >= 640 && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [autoFocus])

  return (
    <div className="flex h-[50px] w-full shrink-0 items-center gap-3.5 border-b border-white/15 px-5 text-white md:h-12 md:gap-4">
      <Icon
        name="magnifier"
        size={14}
        className="flex h-7 w-7 shrink-0 items-center justify-center"
      />
      <input
        ref={inputRef}
        type="text"
        placeholder={`Search ${collection.label}...`}
        className="h-full w-full bg-transparent text-16 placeholder:text-white/40 focus:outline-none md:text-13"
        onChange={(e) => setSearchAction(e.target.value)}
      />
    </div>
  )
}
